// Project-wide design tokens.
// All styled components import from here — no hardcoded style values.
// When a ThemeProvider is set up, replace this import
// with useTheme() or styled(Comp)(({ theme }) => ({ ... })).

const BORDER_COLOR = '#d1d5db';
const BORDER_LIGHT = '#e5e7eb';
// The app's one blue: active toggle states and every map's route line.
const ACCENT_BLUE = '#1976D2';

const theme = {
  colors: {
    primary: '#368007',
    primaryHover: '#2a6605',
    danger: '#dc2626',
    dangerBorder: '#fecaca',
    dangerHover: '#b91c1c',

    text: '#374151',
    textDark: '#1a1a1a',
    textBody: '#1f2937',
    textMuted: '#6b7280',
    textHint: '#9ca3af',

    border: BORDER_COLOR,
    borderLight: BORDER_LIGHT,

    bgPage: '#f3f4f6',
    bgCard: '#ffffff',
    bgMuted: '#f9fafb',

    // Skeleton placeholders. Two weights: `skeleton` for text-sized bars, `skeletonSoft`
    // for large blocks, which read as heavy at the darker step. Each pairs with the
    // lighter sheen that sweeps across it.
    skeleton: '#ececec',
    skeletonSheen: '#f6f6f6',
    skeletonSoft: '#f3f4f6',
    skeletonSoftSheen: '#fafafa',

    badgeAm: '#fff3e0',
    badgeAmText: '#e65100',
    badgePm: '#e3f2fd',
    badgePmText: '#1565c0',
    badgeScheduled: '#7c3aed',
    badgeScheduledText: '#ffffff',

    // Blue accent for active/selected toggle states (Route Operations
    // day-phase toggle). Distinct from the green brand `primary`; `accentSoftBg`
    // is the light-blue hover wash.
    accent: ACCENT_BLUE,
    accentSoftBg: '#f5faff',

    // Fleet Status install badges. Violet on purpose: green/red/amber/grey all
    // carry health meaning on that page, and an install note is not a status.
    badgeInstalled: '#ede9fe',
    badgeInstalledText: '#6d28d9',

    warning: '#ff9800',
    warningHover: '#ef6c00',
    warningText: '#f57c00',
    warningBg: '#fff3e0',
    warningCalloutBg: '#fff7ed',
    warningCalloutBorder: '#fed7aa',
    warningCalloutAccent: '#c2410c',
    warningCalloutText: '#7c2d12',
    dangerBg: '#fef2f2',
    dangerHoverBg: '#ffebee',

    success: '#43A047',
    successText: '#1B5E20',
    successBg: '#e8f5e9',

    // Access-badge pill (Badge Name column, View Routes By Date) — violet so
    // it reads as credentials, distinct from tier/status colors.
    accessBadgeBg: '#ede7f6',
    accessBadgeText: '#5e35b1',
    accessBadgeBorder: '#d1c4e9',

    // Yellow "caution" palette — less urgent than warning's orange, used
    // for soft callouts (pinned stops, missing-stop review prompts,
    // delay-impact assessments).
    caution: '#FFB300',
    cautionBg: '#fff8e1',
    cautionText: '#E65100',

    // Deep-orange brand palette for the "Consolidate Selected" / "Send to
    // NextMV" action buttons. Distinct from `warning` (UI urgency) and
    // reused between the route-ops header button and the consolidation
    // modal CTA.
    consolidate: '#FF8A00',
    consolidateHover: '#E67A00',

    disabled: '#9ca3af',

    accentAmber: '#b45309',
    accentPurple: '#7c3aed',

    white: '#ffffff',
    whiteOverlay: 'rgba(255,255,255,0.2)',

    // Live route monitoring status tiers.
    statusOnTrack: '#047857',
    statusOnTrackBg: '#ecfdf5',
    statusOnTrackSoft: '#10b981',
    statusBehind: '#b45309',
    statusBehindBg: '#fef3c7',
    statusBehindSoft: '#d97706',
    statusOffTrack: '#b91c1c',
    statusOffTrackBg: '#fee2e2',
    statusOffTrackSoft: '#dc2626',
    statusCompleted: '#14b8a6',
    statusCompletedBg: '#ccfbf1',
    statusNeutral: '#6b7280',
    statusNeutralBg: '#f4f3ed',
    statusPending: '#d6d3c8',
    // Stops a driver visited out of planned sequence (e.g. did stop 4 before
    // stop 1). Violet so it reads as distinct from the green/amber/red tiers.
    statusOutOfSequence: '#7c3aed',
    statusOutOfSequenceBg: '#f3e8ff',

    // The one line color on the Monitoring route map — the same blue the
    // Route Operations stops map draws its route in, so the driven path reads
    // like a familiar directions line on the basemap.
    routeLine: ACCENT_BLUE,
    // Ink for the map's stop discs, rings and predicted (dashed) paths: the
    // route blue is reserved for where the truck has actually been.
    markerInk: '#1a1a1a',
    // Stocked stop disc.
    stopStocked: '#047857',

    // Informational blue — focus rings/glows on interactive elements
    // (replaced the old focusRing token).
    infoBlue: '#235a98',
    infoBg: '#eff6ff',
    infoBorder: '#bfdbfe',
    // Dimmed backdrop behind modal dialogs.
    scrim: 'rgba(26, 29, 35, 0.55)',
  },

  radii: {
    card: '12px',
    inner: '8px',
    input: '6px',
    pill: '12px',
    badge: '6px',
    small: '4px',
    circle: '50%',
    round: '999px',
  },

  fontWeights: {
    normal: 400,
    medium: 500,
    semibold: 600,
  },

  fonts: {
    system: "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
    mono: 'ui-monospace, SFMono-Regular, monospace',
  },

  spacing: {
    listIndent: '1.15rem',
    // Page layout
    pagePadding: '32px 84px',
    // For dashboards mounted under PageViewContainer (which already
    // contributes the 0 / 84px horizontal gutter) — applying full
    // `pagePadding` would double the horizontal padding and narrow the
    // content. Keep only the vertical breathing room here.
    pagePaddingVertical: '32px 0',
    cardPadding: '32px',
    sectionGap: '24px',
    fieldGap: '16px',
    headerPadding: '14px 24px',
    bodyPadding: '20px',
    toolbarGap: '18px',

    // Component internals
    cellPadding: '12px 16px',
    inputPadding: '7px 10px',
    badgePadding: '2px 10px',
    smallBadgePadding: '2px 8px',
    buttonPadding: '12px 48px',
    mediumButtonPadding: '10px 18px',
    smallButtonPadding: '6px 12px',
    addButtonPadding: '7px 16px',
    iconPadding: '4px',
    pillPadding: '3px 10px',
    emptyStatePadding: '40px',
    dropdownItemPadding: '12px 16px',

    // Generic box paddings (vertical horizontal), smallest to largest —
    // for callouts, chips, and cards that have no dedicated token above.
    paddingSm: '8px 12px',
    paddingMd: '14px 18px',

    // Gaps
    gapXs: '2px',
    gapSm: '4px',
    gapMd: '8px',
    gapLg: '10px',
    gapXl: '12px',
    rowGap: '20px',
    innerGap: '8px',
    // Clears Leaflet's absolutely-positioned popup close button (18px + 4px).
    popupCloseGap: '24px',
    // Leaflet tooltip chrome (map hover cards, GPS-gap labels).
    tooltipPadding: '6px 8px',

    // Small component paddings
    removeBtnPadding: '5px 14px',
    countInputPadding: '6px 10px',
    inputLabelGap: '6px',
    datePickerInnerPadding: '0 6px',
    datePickerInputPadding: '6px 4px',
    inlineCodePadding: '1px 6px',
    tipsBodyPadding: '4px 16px 18px 38px',

    // Margins
    labelGap: '4px',
    hintGap: '3px',
    requiredGap: '2px',
    pageTitleMargin: '30px',
    dropdownOffset: '5px',
    trafficBodyPadding: '10px',
  },

  typography: {
    pageTitle: { fontSize: '28px', fontWeight: 600 },
    statValue: { fontSize: '32px', fontWeight: 600 },
    sectionTitle: { fontSize: '20px', fontWeight: 600 },
    sectionHeader: { fontSize: '15px', fontWeight: 600 },
    label: { fontSize: '13px', fontWeight: 500 },
    hint: { fontSize: '11px', fontWeight: 400 },
    body: { fontSize: '14px', fontWeight: 400 },
    button: { fontSize: '15px', fontWeight: 500 },
    smallButton: { fontSize: '13px', fontWeight: 500 },
    badge: { fontSize: '11px', fontWeight: 500 },
    // Tiny all-caps tracking label — used inside stat boxes and table headers.
    microLabel: { fontSize: '10px', fontWeight: 600, letterSpacing: '0.4px' },
    tips: { fontSize: '13.5px', fontWeight: 400 },
    inlineCode: { fontSize: '12px', fontWeight: 400 },
  },

  lineHeights: {
    tight: 1, // single-line UI (badges, drag handles)
    compact: 1.3, // narrow stacked labels (refresh-meta column)
    body: 1.5, // readable paragraph text (callouts, descriptions)
  },

  opacities: {
    // Present but deliberately secondary — legend swatches and other marks
    // for inferred or missing data, matching the faint map lines they stand
    // for.
    faint: 0.6,
  },

  shadows: {
    card: '0 1px 3px rgba(0, 0, 0, 0.12)',
    buttonHover: '0 4px 8px rgba(54, 128, 7, 0.3)',
    dropdown: '0px 8px 16px 0px rgba(0,0,0,0.2)',
    // Route-map divIcon accents (Leaflet renders these outside React's tree,
    // so they're interpolated into HTML strings rather than emotion styles).
    markerLabel: '0 1px 2px rgba(0,0,0,0.08)',
    markerGlyph: 'drop-shadow(0 1px 2px rgba(0,0,0,0.25))',
    stopGlyph: '0 1px 3px rgba(20,22,29,0.25)',
    arrowGlyph: 'drop-shadow(0 1px 2px rgba(20,22,29,0.4))',
  },

  borders: {
    input: `1.5px solid ${BORDER_COLOR}`,
    card: `1px solid ${BORDER_LIGHT}`,
  },

  sizes: {
    icon: '14px',
    iconSm: '12px',
    iconXs: '8px',
    iconLg: '18px',
    iconXl: '20px',
    // Type scale (px) — pair with fontWeights/lineHeights tokens.
    microFontSize: '10px',
    captionFontSize: '11px',
    bodyFontSize: '14px',
    subheadFontSize: '15px',
    headingFontSize: '16px',
    statFontSize: '22px',
    heroStatFontSize: '34px',
    inputMinHeight: '34px',
    selectMinHeight: '40px',
    textareaMinHeight: '60px',
    smallFontSize: '12px',
    tabMinWidth: '120px',
    filterFieldMinWidth: '200px',
    // The two absence cards on Fleet Status are narrower
    summaryCardMinorMinWidth: '150px',
    // Wide enough to read a pasted list of location ids, not so wide it spans
    // the card on its own row.
    searchFieldMaxWidth: '30rem',
    // Keeps explanatory copy near 65 characters wide on a full-width page.
    proseMaxWidth: '46ch',
    // Below this a column can't hold a readable line, so the grid drops to one.
    explainerColMinWidth: '19rem',
    // Three explainer columns plus the gaps between them; without a cap each
    // line runs past a comfortable measure on a wide screen.
    explainerMaxWidth: '72rem',
    // Holds two date inputs side by side before the range wraps.
    dateRangeFilterMinWidth: '320px',
    regionSelectWidth: '220px',
    marketSelectWidth: '280px',
    // Missed-stop card: right-hand decision column (Reroute checkbox +
    // deselect-reason select) — wide enough that the select doesn't wrap.
    decisionColumnMinWidth: '180px',
    // Route Operations route-card map view (mock 010 dp-route cards).
    routeCardMapHeight: '400px',
    // Numbered stop marker disc on route maps.
    mapMarkerSize: '28px',
    driverSelectWidth: '220px',
    // Numbered stop disc in stop lists (Dispatch tab rows, confirm modal)
    // and the selection checkboxes beside them.
    stopBadgeSize: '28px',
    checkboxSize: '18px',
    // Monitoring tab route-map: min height of the Leaflet viewport and the
    // surrounding pane. Bumped so the map shows more of the route at once.
    mapMinHeight: '640px',
    mapPaneMinHeight: '700px',
    // Monitoring Global view: right-hand route panel, open and collapsed.
    monitoringPanelWidth: '360px',
    monitoringPanelRailWidth: '44px',
    // Generic narrow max-width for callouts, empty states, and short prose.
    maxWidthSm: '360px',
    // Small square map control button (Monitoring Recenter), sized to match
    // Leaflet's own zoom control buttons.
    mapControlSize: '32px',
    // Thin legend swatch rule, for the map lines drawn lighter than the
    // default 3px stroke (GPS gaps, other-driver hops).
    legendLineWidth: '2px',
    // Floor for the fleet table: below this its thirteen columns compress into
    // slivers that wrap mid-word. The wrapper scrolls instead.
    fleetTableMinWidth: '1600px',
    // Long reason lists wrap at their separators rather than widening the table.
    fleetReasonsMaxWidth: '260px',
    // Ceiling for the frozen Location column on a phone, where a column sized
    // to its content left no room for the twelve it is meant to anchor.
    fleetStickyColMaxWidth: '45vw',
  },

  transitions: {
    default: 'all 0.2s ease',
  },

  filters: {
    // Desaturates the OpenStreetMap basemap toward a light-grey navigation
    // look (OSM's stock style is loudly colored) so the data layers drawn on
    // top carry the color. Apply to Leaflet's tile pane only — markers and
    // lines render in their own panes and stay vivid.
    mutedBasemap: 'grayscale(0.75) saturate(0.7) brightness(1.06)',
  },

  breakpoints: {
    mobile: '768px',
    // Two-up layouts (side-by-side route cards) only above this width.
    desktop: '1280px',
  },

  zIndices: {
    // Leaflet's marker/popup panes run 600–700 and its controls run 1000.
    // Popovers (date picker calendar, custom dropdowns) must sit above all of them.
    dropdown: 1100,
    // Sticky in-page toolbars (Dispatch tab action bar) — above the cards
    // they scroll over, below every popover.
    stickyToolbar: 5,
  },
};

// Status pill tones — used by the StopStatusPill atom to color
// driver-action stop statuses (Stocked, In progress, Unreachable, etc.).
// Keyed by semantic tone, not by status ID, so the same map covers Route
// Operations, View Stocking Dashboard, and any future surface that needs
// status pills.
// `on-track` and `neutral` mirror the existing success / muted palettes;
// `in-progress` (amber) and `off-track` (red) are this map's only new hexes.
theme.statusPillTones = {
  'on-track': {
    fg: theme.colors.successText,
    bg: theme.colors.successBg,
    softBg: theme.colors.success,
  },
  'in-progress': {
    fg: '#b45309',
    bg: '#fef3c7',
    softBg: '#d97706',
  },
  'off-track': {
    fg: '#b91c1c',
    bg: '#fee2e2',
    softBg: '#dc2626',
  },
  neutral: {
    fg: theme.colors.textMuted,
    bg: theme.colors.bgMuted,
    softBg: theme.colors.textHint,
  },
};

export default theme;
